import React, { useState, useCallback, useEffect, useContext } from 'react';
import SEO from '../../components/SEO/SEO';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import spinner from '../../assets/Spinner.svg';

import { useAuthUser } from '../../hooks/useAuthUser';

import style from './Home.module.css';
import StrydCardSmall from '../../components/StrydCardSmall/StrydCardSmall';
import FriendCard from '../../components/FriendCard/FriendCard';

import { MdLocalFireDepartment, MdEmojiEvents, MdSmartToy, MdArrowForward, MdFitnessCenter, MdRestaurant, MdPeople, MdPlayArrow } from 'react-icons/md';
import { UIContext } from '../../contexts/UIContext';
import { UserContext } from '../../contexts/UserContext';
import { findUserRank } from '../../utils/findUserRank';
import rankData from '../../utils/ranks';
import { requestWithApiFallback } from '../../utils/apiRequest';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import formatDisplayName from '../../utils/formatDisplayName';

const DEFAULT_USER_ID = "user_2PSVExD2Jze2iGYg75qSShJe8If";

const Home = () => {
	const { user, isLoaded } = useAuthUser();
	useContext(UIContext);
	const { dbUser } = useContext(UserContext);
	const navigate = useNavigate();

	const API_URL = process.env.REACT_APP_API_URL;
	const [loading, setLoading] = useState(true);
	const [routines, setRoutines] = useState(null);
	const [people, setPeople] = useState(null);
	const [feedPreview, setFeedPreview] = useState([]);
	const [isFeedPreviewLoading, setIsFeedPreviewLoading] = useState(true);
	const [streak, setStreak] = useState(null);
	const [hasResolvedOnboardingRedirect, setHasResolvedOnboardingRedirect] = useState(false);

	const getPeople = useCallback(() => {
		const userId = user?.id || DEFAULT_USER_ID;
		axios.get(`${API_URL}/recommendations/people/${userId}`)
			.then(res => setPeople(res.data))
			.catch(err => console.log('Error fetching people:', err.message));
	}, [API_URL, user]);

	const getRoutines = useCallback(() => {
		const userId = user?.id || DEFAULT_USER_ID;
		axios.get(`${API_URL}/recommendations/routines/${userId}`)
			.then(res => setRoutines(res.data))
			.catch(err => console.log('Error fetching routines:', err.message));
	}, [API_URL, user]);

	const getStreak = useCallback(() => {
		if (user?.id) {
			axios.get(`${API_URL}/streaks/${user.id}`)
				.then(res => setStreak(res.data))
				.catch(() => {});
		}
	}, [API_URL, user]);

	const getFeedPreview = useCallback(() => {
		setIsFeedPreviewLoading(true);
		const endpoint = user?.id
			? `${API_URL}/feed/timeline/${user.id}?limit=3`
			: `${API_URL}/feed/public?limit=3`;
		axios.get(endpoint)
			.then(res => setFeedPreview(res.data.posts || []))
			.catch(() => setFeedPreview([]))
			.finally(() => setIsFeedPreviewLoading(false));
	}, [API_URL, user?.id]);

	useEffect(() => {
		if (isLoaded) {
			getPeople();
			getRoutines();
			getFeedPreview();
			if (user) {
				getStreak();
			}
			setLoading(false);
		}
	}, [isLoaded, user, getPeople, getRoutines, getFeedPreview, getStreak]);

	useEffect(() => {
		if (!isLoaded || !user?.id || hasResolvedOnboardingRedirect) return;

		let isActive = true;
		const resolveOnboardingRoute = async () => {
			try {
				const response = await requestWithApiFallback(API_URL, (base) => axios.get(
					`${base}/onboarding/resume/${encodeURIComponent(user.id)}`,
					{ withCredentials: true }
				));
				const route = response?.data?.route;
				if (route && isActive) {
					navigate(route, { replace: true });
					return;
				}
			} catch (error) {
				console.error('Onboarding resume route check failed:', error);
			}
			if (isActive) {
				setHasResolvedOnboardingRedirect(true);
			}
		};

		resolveOnboardingRoute();
		return () => {
			isActive = false;
		};
	}, [API_URL, hasResolvedOnboardingRedirect, isLoaded, navigate, user?.id]);

	if (!isLoaded || loading) {
		return <div className={style.HomePage}><span className={style.LoadingText}>Loading...</span></div>;
	}

	const authPath = (path) => user ? path : `/login?redirectTo=${encodeURIComponent(path)}`;
	const userRank = dbUser?.totalXP ? findUserRank(dbUser.totalXP) : rankData[0];
	const greetingTime = () => {
		const hr = new Date().getHours();
		if (hr < 12) return 'Good morning';
		if (hr < 18) return 'Good afternoon';
		return 'Good evening';
	};
	const displayName = dbUser?.username
		? dbUser.username.charAt(0).toUpperCase() + dbUser.username.slice(1)
		: '';

	return (
		<div className={style.HomePage}>
			<SEO title="Home" description="Create and explore personalized fitness routines (stryds), discover people, and jump into AI-powered workouts on Calcium." path="/" />

			{user && dbUser?.username && (
				<div className={style.GreetingSection}>
					<div className={style.GreetingLeft}>
						<span className={style.GreetingTime}>{greetingTime()}</span>
						<h1 className={style.GreetingName}>
							{displayName}
							<VerifiedBadge userPlan={dbUser?.userPlan} totalXP={dbUser?.totalXP} size='large' />
						</h1>
					</div>
				</div>
			)}

			{user && (
				<div className={style.StatsRow}>
					<div className={style.StatCard}>
						<div className={style.StatIconWrapper} style={{ background: 'rgba(255, 152, 0, 0.1)' }}>
							<MdLocalFireDepartment style={{ color: '#FF9800' }} />
						</div>
						<div className={style.StatInfo}>
							<span className={style.StatValue}>{streak?.currentStreak || 0}</span>
							<span className={style.StatLabel}>Day Streak</span>
						</div>
					</div>

					<div className={style.StatCard}>
						<div className={style.StatIconWrapper} style={{ background: 'rgba(76, 175, 80, 0.1)' }}>
							<MdEmojiEvents style={{ color: '#4CAF50' }} />
						</div>
						<div className={style.StatInfo}>
							<span className={style.StatValue}>{(dbUser?.totalXP || 0).toLocaleString()}</span>
							<span className={style.StatLabel}>Total XP</span>
						</div>
					</div>

					<div className={`${style.StatCard} ${style.RankStatCard}`}>
						<div className={style.StatIconWrapper} style={{ background: 'rgba(156, 39, 176, 0.1)' }}>
							<span style={{ fontSize: '1.1rem' }}>{userRank?.emoji || '🥔'}</span>
						</div>
						<div className={style.StatInfo}>
							<span className={`${style.StatValue} ${style.RankStatValue}`}>{userRank?.rank || 'Couch Potato'}</span>
							<span className={style.StatLabel}>Rank</span>
						</div>
					</div>
				</div>
			)}

			{user && dbUser?.lastWorkout?.routineName && dbUser?.lastWorkout?.routineCreatorID && (
				<Link
					to={`/stryd/${dbUser.lastWorkout.routineCreatorID}/${encodeURIComponent(dbUser.lastWorkout.routineName)}`}
					className={style.PickUpCard}
				>
					<div className={style.PickUpIcon}>
						<MdPlayArrow />
					</div>
					<div className={style.PickUpContent}>
						<span className={style.PickUpLabel}>Pick up where you left off</span>
						<span className={style.PickUpRoutine}>{dbUser.lastWorkout.routineName}</span>
					</div>
					<MdArrowForward className={style.PickUpChevron} />
				</Link>
			)}

			<div className={style.QuickAccessGrid}>
				<Link to={authPath('/profile-page')} className={style.QuickCard}>
					<div className={style.QuickCardIcon}><MdFitnessCenter /></div>
					<span className={style.QuickCardText}>My Routines</span>
				</Link>
				<Link to={authPath('/people')} className={style.QuickCard}>
					<div className={style.QuickCardIcon}><MdPeople /></div>
					<span className={style.QuickCardText}>Find People</span>
				</Link>
				<Link to={authPath('/cali-chat')} className={style.QuickCardCali}>
					<div className={style.QuickCardIcon}><MdSmartToy /></div>
					<span className={style.QuickCardText}>Ask Cali</span>
				</Link>
				<Link to={authPath('/meals')} className={style.QuickCard}>
					<div className={style.QuickCardIcon}><MdRestaurant /></div>
					<span className={style.QuickCardText}>Meal Plans</span>
				</Link>
			</div>

			<div className={style.HomePageContent}>
				<div className={style.CardsWrapper}>
					<div className={style.SectionHeader}>
						<span className={style.CardsTitle}>Explore Routines</span>
						<Link to="/explore-routines" className={style.SeeAllLink}>
							See all <MdArrowForward />
						</Link>
					</div>

					{routines !== undefined ? (
						Array.isArray(routines) && routines.length > 0 ? (
							<div className={style.CardsContainer}>
								{routines.slice(0, 4).map(routine => (
									<Link
										key={routine.userID + routine.routine.routineName}
										to={`/stryd/${routine.userID}/${encodeURIComponent(routine.routine.routineName)}`}
									>
										<StrydCardSmall
											name={routine.routine.routineName}
											time={routine.routine.estimatedTimeMinutes}
											creator={routine.username}
											userID={routine.userID}
										/>
									</Link>
								))}
							</div>
						) : (
							<span className={style.EmptyText}>No routines available</span>
						)
					) : (
						<div className={style.SpinnerWrapper}><img src={spinner} alt="" /></div>
					)}
				</div>

				<div className={style.CardsWrapper}>
					<div className={style.SectionHeader}>
						<span className={style.CardsTitle}>Community Feed</span>
						<Link to="/feed" className={style.SeeAllLink}>
							Open feed <MdArrowForward />
						</Link>
					</div>
					<div className={style.FeedPreviewList}>
						{isFeedPreviewLoading ? (
							<div className={style.SpinnerWrapper}><img src={spinner} alt="" /></div>
						) : feedPreview.length > 0 ? feedPreview.map(post => (
							<Link to="/feed" key={post._id} className={style.FeedPreviewCard}>
								<div className={style.FeedPreviewTop}>
									<span className={style.FeedPreviewUser}>
										{formatDisplayName(post.username)}
										<VerifiedBadge userPlan={post.userPlan} totalXP={post.totalXP} size='small' />
									</span>
									<span className={style.FeedPreviewType}>{post.type?.replace('_', ' ')}</span>
								</div>
								<span className={style.FeedPreviewText}>{post.content}</span>
							</Link>
						)) : (
							<span className={style.EmptyText}>No posts yet</span>
						)}
					</div>
				</div>

				<div className={style.CardsWrapper}>
					<div className={style.SectionHeader}>
						<span className={style.CardsTitle}>Suggested People</span>
						<Link to="/people" className={style.SeeAllLink}>
							See all <MdArrowForward />
						</Link>
					</div>

					{people ? (
						<div className={style.CardsContainer}>
							{people.slice(0, 4).map(person => (
								<Link to={`/profile/${person.userID}`} key={person.userID}>
									<FriendCard
										username={person.username}
										id={person.userID}
										profileImage={person.profileImageLink}
										userPlan={person.userPlan}
										totalXP={person.totalXP}
									/>
								</Link>
							))}
						</div>
					) : (
						<div className={style.SpinnerWrapper}><img src={spinner} alt="" /></div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Home;
